import { Injectable } from '@angular/core';
import { Observable, defer, from, of } from 'rxjs';
import { finalize, shareReplay, switchMap, tap } from 'rxjs/operators';

const DB_NAME = 'nav-report-deck-cache';
const STORE = 'entries';
const MAX_AGE_MS = 8 * 60 * 60 * 1000;
/** Parsed values kept in this tab, so a page of thumbnails on one source doesn't re-read it each time. */
const MEMORY_ENTRIES = 24;

interface CacheEntry {
  key: string;
  owner: string;
  savedAt: number;
  sourceId?: string;
  value: unknown;
}

/** Whose session the cache belongs to: nothing is read or written without one. */
function currentOwner(): string | null {
  try {
    const user = JSON.parse(localStorage.getItem('user') ?? 'null') as { id?: string } | null;
    return user?.id ?? null;
  } catch {
    return null;
  }
}

/** A reload is someone asking to see things fresh, so it empties the cache rather than reading it. */
function pageWasReloaded(): boolean {
  const navigation = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined;
  return navigation?.type === 'reload';
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Called on logout. Best effort: logout reloads the page straight after, so entries are also tied to
 * the user who wrote them and are never read back by anyone else.
 */
export function clearReportDeckCache(): void {
  try {
    indexedDB.deleteDatabase(DB_NAME);
  } catch {
    // No IndexedDB (a private window in some browsers): there was nothing to clear.
  }
}

/**
 * A report's design, respondent data and survey layout, kept in the browser so the list's thumbnails
 * and the report they open in a new tab load them once between them.
 *
 * IndexedDB rather than memory because the report opens in a new tab, which is a fresh copy of the
 * app. Every key carries what makes it current -- the design's version, the data's fetched-at time --
 * so a changed report or a refreshed source is simply a miss. Entries expire after a few hours, go on
 * logout or a page reload, and any failure here falls back to the network.
 */
@Injectable({ providedIn: 'root' })
export class ReportDeckCache {
  private db: Promise<IDBDatabase | null> | null = null;
  private memory = new Map<string, unknown>();
  private inFlight = new Map<string, Observable<unknown>>();

  /**
   * The cached value for `key`, or `fetch()`'s result, stored for next time. Concurrent asks for the
   * same key share one fetch. `sourceId` ties an entry to a data source so a refresh can drop it.
   */
  get<T>(key: string, fetch: () => Observable<T>, sourceId?: string): Observable<T> {
    const owner = currentOwner();
    if (!owner) return fetch();

    const memoryKey = `${owner}|${key}`;
    if (this.memory.has(memoryKey)) return of(this.memory.get(memoryKey) as T);

    const pending = this.inFlight.get(memoryKey);
    if (pending) return pending as Observable<T>;

    const load$ = defer(() => from(this.read(owner, key))).pipe(
      switchMap(hit => hit !== undefined
        ? of(hit as T)
        : fetch().pipe(tap(value => this.write(owner, key, value, sourceId)))),
      tap(value => this.remember(memoryKey, value)),
      finalize(() => this.inFlight.delete(memoryKey)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.inFlight.set(memoryKey, load$);
    return load$;
  }

  /** Stores a value already in hand, such as a design just saved. */
  put(key: string, value: unknown, sourceId?: string): void {
    const owner = currentOwner();
    if (!owner) return;
    this.remember(`${owner}|${key}`, value);
    this.write(owner, key, value, sourceId);
  }

  /** Drops everything read from a data source, when it is refreshed or deleted. */
  forgetSource(sourceId: string): void {
    for (const key of [...this.memory.keys()]) {
      if (key.includes(`:${sourceId}:`)) this.memory.delete(key);
    }
    this.deleteWhere(entry => entry.sourceId === sourceId);
  }

  private remember(memoryKey: string, value: unknown): void {
    this.memory.delete(memoryKey);
    this.memory.set(memoryKey, value);
    while (this.memory.size > MEMORY_ENTRIES) {
      const oldest = this.memory.keys().next().value;
      if (oldest === undefined) break;
      this.memory.delete(oldest);
    }
  }

  private open(): Promise<IDBDatabase | null> {
    this.db ??= new Promise<IDBDatabase | null>(resolve => {
      try {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'key' });
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    }).then(async db => {
      if (!db) return null;
      const owner = currentOwner();
      const reloaded = pageWasReloaded();
      const now = Date.now();
      await this.deleteWhere(entry => reloaded || entry.owner !== owner || now - entry.savedAt > MAX_AGE_MS, db);
      return db;
    });
    return this.db;
  }

  private async read(owner: string, key: string): Promise<unknown> {
    try {
      const db = await this.open();
      if (!db) return undefined;
      const entry = await request<CacheEntry | undefined>(
        db.transaction(STORE, 'readonly').objectStore(STORE).get(this.storeKey(owner, key)));
      if (!entry || entry.owner !== owner || Date.now() - entry.savedAt > MAX_AGE_MS) return undefined;
      return entry.value;
    } catch {
      return undefined;
    }
  }

  private async write(owner: string, key: string, value: unknown, sourceId?: string): Promise<void> {
    try {
      const db = await this.open();
      if (!db) return;
      const entry: CacheEntry = { key: this.storeKey(owner, key), owner, savedAt: Date.now(), sourceId, value };
      await request(db.transaction(STORE, 'readwrite').objectStore(STORE).put(entry));
    } catch {
      // Full, blocked or gone: the next load just fetches again.
    }
  }

  /** Deletes every stored entry `doomed` picks. */
  private async deleteWhere(doomed: (entry: CacheEntry) => boolean, opened?: IDBDatabase): Promise<void> {
    try {
      const db = opened ?? await this.open();
      if (!db) return;
      await new Promise<void>(resolve => {
        const req = db.transaction(STORE, 'readwrite').objectStore(STORE).openCursor();
        req.onsuccess = () => {
          const cursor = req.result;
          if (!cursor) return resolve();
          if (doomed(cursor.value as CacheEntry)) cursor.delete();
          cursor.continue();
        };
        req.onerror = () => resolve();
      });
    } catch {
      // Nothing to tidy if the store can't be opened.
    }
  }

  private storeKey(owner: string, key: string): string {
    return `${owner}|${key}`;
  }
}
